/**
 * A portal's typeface, served by Tonnie (6.8): the faces ship with the page
 * that wears them — calc.tonnie.ai for the calculator, the operation's own
 * app for its preview — never fetched from a third party at the visitor's
 * request (no visitor's address handed to a font host from a partner's page,
 * and nothing for the partner's CSP to allow).
 *
 * The weights the calculator sets (400, 500, 600, 700) — those the face has:
 * Lato is drawn at 400 and 700 only, and the browser takes the nearest.
 * Each face is its own chunk, loaded only when a portal wears it.
 */
import type { InOutPortalTheme } from '@tonnie/types';

export type InOutPortalTypeface = Exclude<InOutPortalTheme['font'], 'system'>;

const FACES: Record<InOutPortalTypeface, () => Promise<unknown>> = {
  Inter: () =>
    Promise.all([
      import('@fontsource/inter/400.css'),
      import('@fontsource/inter/500.css'),
      import('@fontsource/inter/600.css'),
      import('@fontsource/inter/700.css'),
    ]),
  'DM Sans': () =>
    Promise.all([
      import('@fontsource/dm-sans/400.css'),
      import('@fontsource/dm-sans/500.css'),
      import('@fontsource/dm-sans/600.css'),
      import('@fontsource/dm-sans/700.css'),
    ]),
  Lato: () => Promise.all([import('@fontsource/lato/400.css'), import('@fontsource/lato/700.css')]),
  Montserrat: () =>
    Promise.all([
      import('@fontsource/montserrat/400.css'),
      import('@fontsource/montserrat/500.css'),
      import('@fontsource/montserrat/600.css'),
      import('@fontsource/montserrat/700.css'),
    ]),
  Nunito: () =>
    Promise.all([
      import('@fontsource/nunito/400.css'),
      import('@fontsource/nunito/500.css'),
      import('@fontsource/nunito/600.css'),
      import('@fontsource/nunito/700.css'),
    ]),
  Poppins: () =>
    Promise.all([
      import('@fontsource/poppins/400.css'),
      import('@fontsource/poppins/500.css'),
      import('@fontsource/poppins/600.css'),
      import('@fontsource/poppins/700.css'),
    ]),
  Rubik: () =>
    Promise.all([
      import('@fontsource/rubik/400.css'),
      import('@fontsource/rubik/500.css'),
      import('@fontsource/rubik/600.css'),
      import('@fontsource/rubik/700.css'),
    ]),
  'Source Sans 3': () =>
    Promise.all([
      import('@fontsource/source-sans-3/400.css'),
      import('@fontsource/source-sans-3/500.css'),
      import('@fontsource/source-sans-3/600.css'),
      import('@fontsource/source-sans-3/700.css'),
    ]),
};

const loaded = new Map<InOutPortalTypeface, Promise<unknown>>();

/**
 * Loads a typeface's faces into the page — once, however often it is asked
 * for. A load that fails is not remembered, so the next ask tries again; until
 * then the page reads in the stack's system face.
 */
export function loadPortalTypeface(face: InOutPortalTypeface): Promise<unknown> {
  let loading = loaded.get(face);
  if (!loading) {
    loading = FACES[face]().catch((error: unknown) => {
      loaded.delete(face);
      throw error;
    });
    loaded.set(face, loading);
  }
  return loading;
}
